import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import app from "../../configs/FirebaseConfig";
import { onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { getCmsRole, getScope } from "../../functions/permissions";
import { report, setGuardianContext } from "../../functions/api/guardian";
import { profileRetryDelayMs } from "../../functions/cmsSession";

const auth = app.auth();
const firestore = app.firestore();

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    loading: true,
    isAuthenticated: false,
    profile: null,
    profileLoading: true,
    // Fout van de profiel-listener. Het profiel blijft dan het laatst bekende;
    // RequireCmsRole logt hier NIET op uit (functions/cmsSession.js).
    profileError: null,
  });
  // Elke ophoging start de profiel-listener opnieuw (na een fout).
  const [profileAttempt, setProfileAttempt] = useState(0);
  const failedAttempts = useRef(0);
  const retryTimer = useRef(null);
  // Alleen voor de melding: had deze sessie al een profiel toen de listener faalde?
  const hadProfile = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthState((prev) => ({
        ...prev,
        user,
        loading: false,
        isAuthenticated: !!user,
        profile: user ? prev.profile : null,
        profileLoading: !!user,
        profileError: null,
      }));
    });

    return () => unsubscribe();
  }, []);

  // Fetch user profile from Firestore when user is authenticated
  useEffect(() => {
    let unsubscribeProfile = null;

    const uid = authState.user?.uid;
    if (uid) {
      const userDocRef = doc(firestore, "users", uid);

      // Use onSnapshot for real-time updates to profile data
      unsubscribeProfile = onSnapshot(
        userDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const profile = docSnapshot.data();
            // Rol en scope bij elke Guardian-melding. Dit moet hier gebeuren en
            // niet in een scherm: fouten tijdens het laden vallen voordat de
            // layout gemount is, en juist dan wil je weten wie het treft -- de
            // bug die hiertoe leidde raakte alleen niet-beheerders.
            setGuardianContext({ cmsRole: getCmsRole(profile) || "geen", scope: getScope(profile).type });
            failedAttempts.current = 0;
            hadProfile.current = true;
            setAuthState((prev) => ({
              ...prev,
              profile,
              profileLoading: false,
              profileError: null,
            }));
          } else {
            failedAttempts.current = 0;
            setAuthState((prev) => ({
              ...prev,
              profile: null,
              profileLoading: false,
              profileError: null,
            }));
          }
        },
        (error) => {
          // Na uitloggen weigert Firestore deze listener nog één keer, voordat
          // de cleanup hem opruimt. Dat is geen fout.
          if (auth.currentUser?.uid !== uid) return;
          console.error("Error fetching user profile:", error);
          // Een dode listener is geen reden om iemand uit te loggen: houd het
          // laatst bekende profiel en probeer het opnieuw. Tot 13-10-2026 zette
          // dit het profiel op null, waarna RequireCmsRole uitlogde -- stil.
          failedAttempts.current += 1;
          const attempt = failedAttempts.current;
          if (attempt === 1) {
            report({
              severity: "error",
              type: "profile_listener_error",
              title: "Profiel van de ingelogde gebruiker kon niet worden geladen",
              context: { code: error?.code, message: error?.message, hadProfile: hadProfile.current },
            });
          }
          setAuthState((prev) => ({
            ...prev,
            profileLoading: false,
            profileError: error,
          }));
          retryTimer.current = setTimeout(() => setProfileAttempt((n) => n + 1), profileRetryDelayMs(attempt));
        },
      );
    } else {
      failedAttempts.current = 0;
      hadProfile.current = false;
      setAuthState((prev) => ({
        ...prev,
        profile: null,
        profileLoading: false,
        profileError: null,
      }));
    }

    return () => {
      clearTimeout(retryTimer.current);
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, [authState.user?.uid, profileAttempt]);

  /** Nu opnieuw proberen in plaats van op de volgende automatische poging te wachten. */
  const retryProfile = useCallback(() => {
    clearTimeout(retryTimer.current);
    setAuthState((prev) => ({ ...prev, profileLoading: !prev.profile }));
    setProfileAttempt((n) => n + 1);
  }, []);

  const signOut = () => firebaseSignOut(auth);

  const value = {
    ...authState,
    signOut,
    retryProfile,
    uid: authState.user?.uid,
    email: authState.user?.email,
    // `role` is het ruwe veld op het profiel (kan "webshopUser" zijn voor een
    // gekoppeld account). Voor CMS-toegang altijd `cmsRole` gebruiken, of
    // liever nog usePermissions().can(...).
    role: authState.profile?.role,
    cmsRole: getCmsRole(authState.profile),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
